import React, { useEffect, useRef, useState } from 'react';
import ApiClient from '../../utils/ApiClient';

// PO SFP split (spec 2026-10-10 §D): rack received SFP units of one PO line into one or more
// SFP-section bins. Its own scan field (bin scan selects, unit scan adds one) plus a manual
// "+ Add location" path. One POST commits every allocation in one transaction.

// True while the rack modal is mounted; the main receiving input must not steal focus then.
export function rackModalOpen() {
    return !!document.querySelector('[data-sfp-rack-modal]');
}

export function allocationTotal(allocs) {
    return (allocs || []).reduce((s, a) => s + (a.qty || 0), 0);
}

function addTo(allocs, location, qty) {
    const found = allocs.find(a => a.location === location);
    if (found) return allocs.map(a => a.location === location ? { ...a, qty: a.qty + qty } : a);
    return [...allocs, { location, qty }];
}

// A unit matches any of `gtins` (the line's unitGtins: price-list GTIN first, then the PO-line
// GTIN); falls back to the single `gtin` when unitGtins is absent.
export function applyScan(state, code, { bins, gtin, gtins, rackable }) {
    const c = (code || '').trim();
    if (!c) return state;
    const canonical = bins.find(b => b.toLowerCase() === c.toLowerCase());
    if (canonical) return { ...state, selectedBin: canonical, error: null };
    const accepted = (gtins && gtins.length ? gtins : [gtin]).filter(Boolean);
    if (accepted.includes(c)) {
        if (!state.selectedBin) return { ...state, error: 'Scan a bin first, then the units' };
        if (allocationTotal(state.allocations) + 1 > rackable) {
            return { ...state, error: `Only ${rackable} unit(s) to rack` };
        }
        return { ...state, allocations: addTo(state.allocations, state.selectedBin, 1), error: null };
    }
    if (/^[A-Z]{1,3}-\d+$/i.test(c)) return { ...state, error: `${c} is not an SFP bin` };
    return { ...state, error: `${c} is not this product (expected ${accepted.join(' or ') || 'its UPC'})` };
}

export default function SfpRackModal({ line, onClose, onRacked }) {
    const [bins, setBins] = useState([]);
    const [state, setState] = useState({ allocations: [], selectedBin: null, error: null });
    const [scan, setScan] = useState('');
    const [addBin, setAddBin] = useState('');
    const [addQty, setAddQty] = useState('');
    const [busy, setBusy] = useState(false);
    // Push entries that did not update Amazon (error/skipped): units ARE racked, operator must know.
    const [pushProblems, setPushProblems] = useState(null);
    const scanRef = useRef(null);
    const rackable = line?.rackable || 0;

    useEffect(() => {
        ApiClient.post('/api/sflocalinventorylocations')
            .then(res => setBins((res.data || []).filter(l => l.section_type === 'SFP').map(l => l.location)))
            .catch(() => setState(s => ({ ...s, error: 'Could not load SFP bins' })));
        scanRef.current?.focus();
    }, []);

    const close = () => {
        onClose();
        // Spec: on close, focus returns to the main receiving scan field.
        setTimeout(() => document.querySelector('[data-receive-scan="1"]')?.focus(), 0);
    };

    const onScanKey = (e) => {
        if (e.key !== 'Enter') return;
        setState(s => applyScan(s, scan, { bins, gtin: line.gtin, gtins: line.unitGtins, rackable }));
        setScan('');
    };

    const addLocation = () => {
        const q = parseInt(addQty) || 0;
        if (!addBin || q <= 0) return;
        if (allocationTotal(state.allocations) + q > rackable) {
            setState(s => ({ ...s, error: `Only ${rackable} unit(s) to rack` }));
            return;
        }
        setState(s => ({ ...s, allocations: addTo(s.allocations, addBin, q), error: null }));
        setAddQty('');
    };

    const changeQty = (location, delta) => {
        setState(s => {
            const allocations = s.allocations
                .map(a => a.location === location ? { ...a, qty: a.qty + delta } : a)
                .filter(a => a.qty > 0);
            if (allocationTotal(allocations) > rackable) return { ...s, error: `Only ${rackable} unit(s) to rack` };
            return { ...s, allocations, error: null };
        });
    };

    const submit = async () => {
        setBusy(true);
        try {
            const res = await ApiClient.post('/api/local-sfp/rack', {
                poNumber: line.poNumber, asin: line.asin, allocations: state.allocations,
            });
            const problems = (res.data?.push || [])
                .filter(p => p && (p.status === 'error' || p.status === 'skipped'));
            onRacked(res.data);
            if (problems.length) {
                setPushProblems(problems.map(p => p.message || `Push ${p.status}`));
            } else {
                close();
            }
        } catch (err) {
            setState(s => ({ ...s, error: err?.response?.data?.error || 'Rack failed' }));
        } finally {
            setBusy(false);
        }
    };

    const total = allocationTotal(state.allocations);

    return (
        <div data-sfp-rack-modal="1" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
            <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900">Rack SFP units</h3>
                <p className="text-sm text-gray-600">{line.productName} · {line.asin} · PO {line.poNumber}</p>
                <p className="mt-1 text-sm font-medium text-teal-800">{rackable} units to rack</p>

                <label className="mt-4 block text-xs font-medium text-gray-600">Scan a bin, then each unit</label>
                <input ref={scanRef} value={scan} onChange={e => setScan(e.target.value)} onKeyDown={onScanKey}
                       data-testid="rack-scan-input"
                       className="mt-1 w-full rounded-md border-gray-300 text-sm" placeholder="Bin label or product UPC" />
                {state.selectedBin && <p className="mt-1 text-xs text-gray-500">Bin: <b>{state.selectedBin}</b></p>}

                <div className="mt-3 flex items-end gap-2">
                    <select data-testid="add-bin-select" value={addBin} onChange={e => setAddBin(e.target.value)}
                            className="rounded-md border-gray-300 text-sm">
                        <option value="">Bin…</option>
                        {bins.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <input data-testid="add-bin-qty" type="number" min="1" value={addQty}
                           onChange={e => setAddQty(e.target.value)} className="w-20 rounded-md border-gray-300 text-sm" />
                    <button type="button" onClick={addLocation}
                            className="rounded-md bg-white px-3 py-1.5 text-sm ring-1 ring-gray-300 hover:bg-gray-50">
                        + Add location
                    </button>
                </div>

                <ul className="mt-3 divide-y divide-gray-100 text-sm">
                    {state.allocations.map(a => (
                        <li key={a.location} className="flex items-center justify-between py-1.5">
                            <span className="font-medium">{a.location}</span>
                            <span className="flex items-center gap-2">
                                <button type="button" aria-label={`one fewer in ${a.location}`} onClick={() => changeQty(a.location, -1)}
                                        className="px-2 text-gray-600">−</button>
                                <span>{a.qty}</span>
                                <button type="button" aria-label={`one more in ${a.location}`} onClick={() => changeQty(a.location, 1)}
                                        className="px-2 text-gray-600">+</button>
                                <button type="button" onClick={() => changeQty(a.location, -a.qty)}
                                        className="text-xs text-red-600">remove</button>
                            </span>
                        </li>
                    ))}
                </ul>
                <p className="mt-2 text-sm text-gray-700">Allocated {total} of {rackable}</p>
                {state.error && <p className="mt-2 text-sm text-red-600">{state.error}</p>}
                {pushProblems && (
                    <div data-testid="rack-push-problems" className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        <p className="font-medium">Units racked, but Amazon was not updated:</p>
                        <ul className="mt-1 list-disc pl-5">
                            {pushProblems.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                    </div>
                )}

                <div className="mt-4 flex justify-end gap-2">
                    {pushProblems ? (
                        <button type="button" onClick={close} className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white">Close</button>
                    ) : (
                        <>
                            <button type="button" onClick={close} className="rounded-md px-3 py-2 text-sm ring-1 ring-gray-300">Cancel</button>
                            <button type="button" onClick={submit} disabled={busy || total === 0}
                                    className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                                {busy ? 'Racking…' : 'Rack'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
